import { useState } from 'react';
import directionStyles from './formInputDirection.module.css';
import styles from './PasswordInputRow.module.css';

export type PasswordInputRowProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** Merged with internal layout styles — pass your themed input class (e.g. `styles.textInput`). */
  inputClassName: string;
  autoComplete?: string;
  inputKey?: string | number;
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
  ariaRequired?: boolean;
  disabled?: boolean;
  /** Optional class on the outer flex row (e.g. `flex: 1` inside a parent `.inputRow`). */
  rowClassName?: string;
  /** Optional class on the show/hide password toggle button. */
  toggleClassName?: string;
  /** Password fields are always entered LTR (even on Hebrew UI). */
  forceLtr?: boolean;
};

function EyeIcon({ hidden }: { hidden: boolean }) {
  if (hidden) {
    return (
      <svg
        className={styles.icon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );
  }
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function PasswordInputRow({
  id,
  value,
  onChange,
  inputClassName,
  autoComplete,
  inputKey,
  ariaDescribedBy,
  ariaInvalid,
  ariaRequired,
  disabled = false,
  rowClassName,
  toggleClassName,
  forceLtr = false,
}: PasswordInputRowProps) {
  const [visible, setVisible] = useState(false);
  const inputClass = [
    styles.inputGrow,
    inputClassName,
    forceLtr ? directionStyles.inputLtr : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rowClassName ? `${styles.row} ${rowClassName}` : styles.row}>
      <input
        key={inputKey}
        id={id}
        className={inputClass}
        dir={forceLtr ? 'ltr' : undefined}
        type={visible ? 'text' : 'password'}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired === true ? true : undefined}
      />
      <button
        type="button"
        className={toggleClassName ? `${styles.toggle} ${toggleClassName}` : styles.toggle}
        disabled={disabled}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        aria-controls={id}
      >
        <EyeIcon hidden={visible} />
      </button>
    </div>
  );
}
